/**
 * Paging data for the HRentRoll widget
 */
export class HRentrollWidgetPageData {
  /**
   * Constructor
   * @param masterThis Master widget this context
   * @param originalData Original data
   */
  constructor(masterThis: any, originalData: any = null) {
    this.masterThis = masterThis;
    this.originalData = originalData;
    this.widgetPages = masterThis ? masterThis.widgetPages : [];
    if (originalData) {
      this.displayValuesOverTimeId = originalData.displayValuesOverTimeId;
    }
    //constructor with originalData = null will be called only from onInitializedSlave (just temporary dummy object
    //that will be replaced with real "page object" from master page as response to "getSlaveData")
    if (originalData !== null) {
      this.tenantLeasingCommissionsMonthsLabelOverride = originalData.tenantLeasingCommissionsMonthsLabelOverride;
      this.tenantTenantImprovementsPerSqmLabelOverride = originalData.tenantTenantImprovementsPerSqmLabelOverride;
    }
  }

  /**
   * Master widget's this instance
   * Instance of master HRentRollWidgetComponent
   */
  private masterThis: any;

  /**
   * Original widget data
   * Used to distribute data among widget pages
   */
  private originalData: any;

  /**
   * Array of page data
   */
  private widgetPages: Array<HRentrollWidgetPageData>;

  /**
   * Page number
   */
  public pageNo = 0;

  /**
   * Padding top for footnote
   */
  public footNotePaddingTop = "";

  /**
   * Display let subtotal
   */
  public displayLetSubtotal = false;

  /**
   * Display let subtotal space
   */
  public displayLetSubtotalSpace = false;

  /**
   * Display vacant subtotal
   */
  public displayVacantSubtotal = false;

  /**
   * Display vacant subtotal space
   */
  public displayVacantSubtotalSpace = false;

  /**
   * Display total
   */
  public displayTotal = false;

  /**
   * Let rows
   */
  public rowsLet = [];

  /**
   * Display values over time setting
   */
  public displayValuesOverTimeId = 0;

  /**
   * Vacant rows
   */
  public rowsVacant = [];

  /**
   * Leasing commissions label override
   */
  public tenantLeasingCommissionsMonthsLabelOverride = '';

  /**
   * Tenant improvements label override
   */
  public tenantTenantImprovementsPerSqmLabelOverride = ''

  /**
   * Slave data updated?
   */
  public slaveDataUpdated = false;

  /**
   * lettableAreaSumLet calculation
   */
  get lettableAreaSumLet() { return this.originalData.rowsLet.filter(r => !r.tenantAbsorbed && r.affectsOccupancy === 1).map(r => r.lettableArea).reduce((p, c) => p + c, 0); }

  /**
   * unitsSumLet calculation
   */
  get unitsSumLet() { return this.originalData.rowsLet.filter(r => !r.tenantAbsorbed && r.affectsOccupancy === 1).map(r => r.units).reduce((p, c) => p + c, 0); }

  /**
   * lettableAreaSumVacant calculation
   */
  get lettableAreaSumVacant() { return this.originalData.rowsVacant.filter(r => !r.tenantAbsorbed && r.affectsOccupancy === 1).map(r => r.lettableArea).reduce((p, c) => p + c, 0); }

  /**
   * unitsSumVacant calculation
   */
  get unitsSumVacant() { return this.originalData.rowsVacant.filter(r => !r.tenantAbsorbed && r.affectsOccupancy === 1).map(r => r.units).reduce((p, c) => p + c, 0); }

  /**
   * lettableAreaSumTotal calculation
   */
  get lettableAreaSumTotal() { return this.lettableAreaSumLet + this.lettableAreaSumVacant; }

  /**
   * unitsSumTotal calculation
   */
  get unitsSumTotal() { return this.unitsSumLet + this.unitsSumVacant; }

  /**
   * contractualRentSumLet calculation
   */
  get contractualRentSumLet() { return this.originalData.rowsLet.filter(r => !r.tenantAbsorbed).map(r => r.contractualRent).reduce((p, c) => p + c, 0); }

  /**
   * marketRentSumLet calculation
   */
  get marketRentSumLet() { return this.originalData.rowsLet.filter(r => !r.tenantAbsorbed).map(r => r.marketRent).reduce((p, c) => p + c, 0); }

  /**
   * contractualRentSumVacant calculation
   */
  get contractualRentSumVacant() { return this.originalData.rowsVacant.filter(r => !r.tenantAbsorbed).map(r => r.contractualRent).reduce((p, c) => p + c, 0); }

  /**
   * marketRentSumVacant calculation
   */
  get marketRentSumVacant() { return this.originalData.rowsVacant.filter(r => !r.tenantAbsorbed).map(r => r.marketRent).reduce((p, c) => p + c, 0); }

  /**
   * contractualRentSumTotal calculation
   */
  get contractualRentSumTotal() { return this.contractualRentSumLet + this.contractualRentSumVacant; }

  /**
   * marketRentSumTotal calculation
   */
  get marketRentSumTotal() { return this.marketRentSumLet + this.marketRentSumVacant; }

  /**
   * Gets the new page
   */
  createNewPage(): number {
    const page = new HRentrollWidgetPageData(this.masterThis, this.originalData);
    page.pageNo = this.widgetPages.length;
    this.widgetPages.push(page);
    return this.widgetPages.length - 1;
  }

  /**
   * Gets the rows for the page
   */
  getPageRows() {
    return (this.rowsLet.length + this.rowsVacant.length
      + (this.displayLetSubtotal ? 1 : 0)
      + (this.displayLetSubtotalSpace ? 1 : 0)
      + (this.displayVacantSubtotal ? 1 : 0)
      + (this.displayVacantSubtotalSpace ? 1 : 0)
      + (this.displayTotal ? 1 : 0));
  }

  /**
   * Gets the page
   * @param pageIndex {number} Page Index
   */
  getPage(pageIndex: number) {
    return this.widgetPages[pageIndex];
  }

  //#region Let

  /**
   * Adds a let data row
   * @param pageIndex {number} Page Index
   * @param row {any} Data row
   */
  addLetDataRow(pageIndex: number, row: any) {
    const page = this.getPage(pageIndex);
    page.rowsLet.push(row);
    return page;
  }

  /**
   * Adds let total
   * @param pageIndex {number} Page Index
   */
  addLetTotal(pageIndex: number) {
    const page = this.getPage(pageIndex);
    page.displayLetSubtotal = true;
    return page;
  }

  /**
   * Adds let total space
   * @param pageIndex {number} Page Index
   */
  addLetTotalSpace(pageIndex: number) {
    const page = this.getPage(pageIndex);
    if (page.getPageRows() !== 0) page.displayLetSubtotalSpace = true;
    return page;
  }

  //#endregion

  //#region Vacant

  /**
   * Adds a vacant data row
   * @param pageIndex {number} Page Index
   * @param row {any} Data row
   */
  addVacantDataRow(pageIndex: number, row: any) {
    const page = this.getPage(pageIndex);
    page.rowsVacant.push(row);
    return page;
  }

  /**
   * Adds vacant total
   * @param pageIndex {number} Page Index
   */
  addVacantTotal(pageIndex: number) {
    const page = this.getPage(pageIndex);
    page.displayVacantSubtotal = true;
    return page;
  }

  /**
   * Adds vacat total space
   * @param pageIndex {number} Page Index
   */
  addVacantTotalSpace(pageIndex: number) {
    const page = this.getPage(pageIndex);
    if (page.getPageRows() !== 0) page.displayVacantSubtotalSpace = true;
    return page;
  }

  //#endregion

  /**
   * Adds total
   */
  addTotal(pageIndex: number) {
    const page = this.getPage(pageIndex);
    page.displayTotal = true;
    return page;
  }

  /**
   * Sets footnote padding
   * @param pageIndex {number} Page Index
   * @param paddingTop {number} paddingTop value
   */
  setFootnotePaddingTop(pageIndex: number, paddingTop: number) {
    const page = this.getPage(pageIndex);
    page.footNotePaddingTop = `${paddingTop}px`;
    return page;
  }
}
